import { useCallback, useEffect, useState } from "react";

/**
 * Title summary for list view
 */
export interface TitleSummary {
  id: string;
  slug: string;
  name: string;
  year?: number;
  published: boolean;
  posterUrlHd?: string;
  genres?: string[];
  directorName?: string;
}

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Hook to fetch paginated list of titles
 */
export function useTitles(search?: string, page = 1, pageSize = 20) {
  const [titles, setTitles] = useState<TitleSummary[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTitles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build the full URL
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
      const url = `${baseUrl}/v1/titles`;

      // Use regular fetch since this is a public endpoint
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // The endpoint returns an array directly, not wrapped in an object
      const allTitles = (
        (await response.json()) as Array<{
          id: string;
          slug: string;
          name: string;
          year?: number;
          posterUrlHd?: string;
          genres?: string[];
          directorName?: string;
        }>
      ).map((title) => ({
        ...title,
        published: true, // All titles from /v1/titles are published
      }));

      // Filter by search query if provided
      let filteredTitles = allTitles;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredTitles = allTitles.filter((title) => {
          return (
            title.name.toLowerCase().includes(searchLower) ||
            title.directorName?.toLowerCase().includes(searchLower) ||
            title.genres?.some((g) => g.toLowerCase().includes(searchLower))
          );
        });
      }

      // Calculate pagination
      const total = filteredTitles.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedTitles = filteredTitles.slice(startIndex, endIndex);

      setTitles(paginatedTitles);
      setPagination({
        total,
        page,
        pageSize,
        totalPages,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch titles";
      setError(message);
      console.error("Error fetching titles:", err);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    void fetchTitles();
  }, [fetchTitles]);

  return { titles, pagination, loading, error, refetch: fetchTitles };
}
